import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    CardActions,
    CardContent,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import DateService from "../../services/DateService";
import BooleanService from "../../services/BooleanService";
import NumberService from "../../services/NumberService";
import { saveTemplate, deleteTemplate } from "../../store/templateSlice";
import { fetchCurrencies, selectCurrencies } from "../../store/currencySlice";
import ViewAndEditFormLayout from "./ViewAndEditFormLayout";
import {selectWalletById} from "../../store/walletSlice";
import {selectCategory} from "../../store/categorySlice";
import { TransactionDetailsFields, TransactionTypeTabs } from "./TransactionFormFields";
import BudgetSelect from "../input/BudgetSelect";
import {useTranslation} from "react-i18next";

const RULE_TYPES = {
    TYPE_SIMPLE: "TYPE_SIMPLE",
    TYPE_DAILY: "TYPE_DAILY",
    TYPE_WEEKLY: "TYPE_WEEKLY",
    TYPE_MONTHLY: "TYPE_MONTHLY",
};

const DAYS_OF_WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const DEFAULT_RULE_ERROR = {
    startDate: null,
    endDate: null,
    dailyRule: null,
    weeklyRule: null,
    monthlyRule: null,
    ruleType: null,
};
const DEFAULT_FORM_ERROR = {
    walletFrom: null,
    walletTo: null,
    categoryTo: null,
    categoryFrom: null,
    amount: null,
    amountFrom: null,
    amountTo: null
};

const EditTemplateForm = ({ template, onCancel }) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const currencies = useSelector((state) => selectCurrencies(state));
    const [type, setType] = useState(template.type || "EXPENSE");
    const [description, setDescription] = useState(template.description || "");
    const [walletFromId, setWalletFromId] = useState(template.walletFrom);
    const [walletToId, setWalletToId] = useState(template.walletTo);
    const [categoryToId, setCategoryToId] = useState(template.categoryTo);
    const [categoryFromId, setCategoryFromId] = useState(template.categoryFrom);
    const [budgetId, setBudgetId] = useState(template.budget);
    const [amount, setAmount] = useState(template.valueFrom ?? template.valueTo ?? "");
    const [valueTo, setValueTo] = useState(template.valueTo ?? "");
    const walletFrom = useSelector((state) => selectWalletById(state, walletFromId));
    const walletTo = useSelector((state) => selectWalletById(state, walletToId));

    const sameTransferCurrency =
        type === "TRANSFER" &&
        walletFrom?.currency != null &&
        walletTo?.currency != null &&
        walletFrom.currency === walletTo.currency;

    const [errors, setErrors] = useState({ rule: { ...DEFAULT_RULE_ERROR }, form: { ...DEFAULT_FORM_ERROR } });
    const [startDate, setStartDate] = useState(DateService.getTodayLocalDateString());
    const [endDate, setEndDate] = useState(null);
    const [ruleType, setRuleType] = useState(RULE_TYPES.TYPE_SIMPLE);
    const [dailyRule, setDailyRule] = useState("1");
    const [weeklyRule, setWeeklyRule] = useState(BooleanService.splitWeeklyCalendarDowToBooleanArray(""));
    const [monthlyRule, setMonthlyRule] = useState(BooleanService.splitToBooleanArray("", 31));

    useEffect(() => {
        setDescription(template.description ?? "");
        setType(template.type ?? "EXPENSE");
        setAmount(template.valueFrom ?? template.valueTo ?? "");
        setValueTo(template.valueTo ?? template.valueFrom ?? "");
        if (template.type === "EXPENSE") {
            setWalletFromId(template.walletFrom);
            setCategoryToId(template.categoryTo);
            setBudgetId(template.budget);
        }
        if (template.type === "INCOME" || template.type === "TRANSFER") {
            setWalletToId(template.walletTo);
            setCategoryFromId(template.categoryFrom);
        }
        if (template.type === "TRANSFER") {
            setWalletFromId(template.walletFrom);
            setWalletToId(template.walletTo);
        }
        // setCurrencyFromId(template.currencyFrom ?? null);
        // setCurrencyToId(template.currencyTo ?? null);

        const rule = template.repeatRule ?? template.rule;

        if (rule) {
            const start = rule.startDate ? (typeof rule.startDate === "string" ? rule.startDate.substring(0, 10) : null) : null;
            const end = rule.finalDate ? (typeof rule.finalDate === "string" ? rule.finalDate.substring(0, 10) : null) : (rule.endDate ? (typeof rule.endDate === "string" ? rule.endDate.substring(0, 10) : null) : null);
            setStartDate(start || DateService.getTodayLocalDateString());
            setEndDate(end ?? null);
            setRuleType(rule.repeatType);
            switch (rule.repeatType) {
                case RULE_TYPES.TYPE_DAILY:
                    setDailyRule("" + (rule.selectedIndex ?? "1"));
                    break;
                case RULE_TYPES.TYPE_WEEKLY:
                    setWeeklyRule(BooleanService.splitWeeklyCalendarDowToBooleanArray(rule.selectedIndex ?? ""));
                    break;
                case RULE_TYPES.TYPE_MONTHLY:
                    setMonthlyRule(BooleanService.splitToBooleanArray(rule.selectedIndex ?? "", 31));
                    break;
                default:
                    break;
            }
        } else {
            setRuleType(RULE_TYPES.TYPE_SIMPLE);
            setStartDate(null);
            setEndDate(null);
        }
    }, [template]);

    useEffect(() => {
        dispatch(fetchCurrencies());
    }, [dispatch]);

    const getRuleTypeLabel = (rt) => {
        switch (rt) {
            case RULE_TYPES.TYPE_SIMPLE:
                return t("forms.template.repeatTypes.simple");
            case RULE_TYPES.TYPE_DAILY:
                return t("forms.template.repeatTypes.daily");
            case RULE_TYPES.TYPE_WEEKLY:
                return t("forms.template.repeatTypes.weekly");
            case RULE_TYPES.TYPE_MONTHLY:
                return t("forms.template.repeatTypes.monthly");
            default:
                return "";
        }
    };

    const validateForm = () => {
        const formErrors = {...DEFAULT_FORM_ERROR};
        const isRepeatRule = ruleType !== RULE_TYPES.TYPE_SIMPLE;
        const ruleErrors = {
            startDate: isRepeatRule && !startDate ? "Start date is required" : null,
            endDate: isRepeatRule && startDate && endDate && endDate < startDate ? "End date must be on or after start date" : null,
            ruleType: !ruleType ? "Rule type is required" : null,
            dailyRule: ruleType === RULE_TYPES.TYPE_DAILY && !dailyRule ? "Please select period" : null,
            weeklyRule: ruleType === RULE_TYPES.TYPE_WEEKLY && !weeklyRule.includes(true) ? "Please choose at least one day" : null,
            monthlyRule: ruleType === RULE_TYPES.TYPE_MONTHLY && !monthlyRule.includes(true) ? "Please choose at least one date" : null,
        };
        if (type === "EXPENSE") {
            if (!walletFromId) formErrors.walletFrom = "Wallet is required";
            if (!categoryToId) formErrors.categoryTo = "Category is required";
            if (amount === null || amount === undefined || `${amount}`.trim() === "") {
                formErrors.amount = "Amount is required";
            }
        } else if (type === "INCOME") {
            if (!walletToId) formErrors.walletTo = "Wallet is required";
            if (!categoryFromId) formErrors.categoryFrom = "Category is required";
            if (amount === null || amount === undefined || `${amount}`.trim() === "") {
                formErrors.amount = "Amount is required";
            }
        } else if (type === "TRANSFER") {
            if (!walletFromId) formErrors.walletFrom = "Source wallet is required";
            if (!walletToId) formErrors.walletTo = "Target wallet is required";
            if (walletFromId && walletToId && walletFromId === walletToId) {
                formErrors.walletTo = "Wallets must be different";
            }
            if (sameTransferCurrency) {
                if (amount === null || amount === undefined || `${amount}`.trim() === "") {
                    formErrors.amount = "Amount is required";
                }
            } else {
                if (amount === null || amount === undefined || `${amount}`.trim() === "") {
                    formErrors.amountFrom = "Source amount is required";
                }
                if (valueTo === null || valueTo === undefined || `${valueTo}`.trim() === "") {
                    formErrors.amountTo = "Target amount is required";
                }
            }
        }
        setErrors({ rule: ruleErrors, form: formErrors });
        return !Object.values(formErrors).some(Boolean) && !Object.values(ruleErrors).some(Boolean);
    };

    const onSaveClick = () => {
        if (!validateForm()) return;
        const selectedIndex =
            ruleType === RULE_TYPES.TYPE_DAILY
                ? "" + dailyRule
                : ruleType === RULE_TYPES.TYPE_WEEKLY
                ? BooleanService.toWeeklyCalendarDowCommaSeparated(weeklyRule)
                : ruleType === RULE_TYPES.TYPE_MONTHLY
                ? BooleanService.toCommaSeparated(monthlyRule)
                : null;

        const repeatRule = ruleType === RULE_TYPES.TYPE_SIMPLE
            ? null
            : {
                startDate: startDate,
                finalDate: endDate || null,
                repeatType: ruleType,
                selectedIndex: selectedIndex,
            };
        let payload;
        if (type === "INCOME") {
            payload = {
                id: template.id,
                description: description?.trim() || "",
                type: "INCOME",
                repeatRule,
                valueTo: amount,
                walletTo: walletToId,
                categoryTo: categoryFromId,
                budget: budgetId,
            };
        } else if (type === "EXPENSE") {
            payload = {
                id: template.id,
                description: description?.trim() || "",
                type: "EXPENSE",
                repeatRule,
                valueFrom: amount,
                walletFrom: walletFromId,
                categoryTo: categoryToId,
                budget: budgetId,
            };
        } else {
            payload = {
                id: template.id,
                description: description?.trim() || "",
                type: "TRANSFER",
                repeatRule,
                valueFrom: amount,
                valueTo: valueTo,
                walletFrom: walletFromId,
                walletTo: walletToId,
            };
        }
        dispatch(saveTemplate(payload, () => onCancel()));
    };

    const changeDailyRule = (day) => {
        setDailyRule(day);
        setErrors((prev) => ({ ...prev, rule: { ...DEFAULT_RULE_ERROR } }));
    };
    const changeWeeklyRule = (day) => {
        const next = [...weeklyRule];
        next[day] = !next[day];
        setWeeklyRule(next);
        setErrors((prev) => ({ ...prev, rule: { ...DEFAULT_RULE_ERROR } }));
    };
    const changeMonthlyRule = (day) => {
        const next = [...monthlyRule];
        next[day] = !next[day];
        setMonthlyRule(next);
        setErrors((prev) => ({ ...prev, rule: { ...DEFAULT_RULE_ERROR } }));
    };
    const changeRuleType = (newType) => {
        setRuleType(newType);
        setErrors((prev) => ({ ...prev, rule: { ...DEFAULT_RULE_ERROR } }));
    };

    const editContent = ({ onCancel: handleCancel, onSave: handleSave }) => (
        <>
            <CardContent sx={{ minHeight: 200, overflowY: "auto", maxHeight: "65%" }}>
            <TransactionTypeTabs type={type} onChange={setType} ariaLabel={t("forms.template.typeTabsAria")} />
            <Box sx={{ m: 2 }}>
                <TextField
                    id="template-name"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    label={t("common.labels.name")}
                    variant="standard"
                    fullWidth
                />
            </Box>
            <TransactionDetailsFields
                type={type}
                walletFromId={walletFromId}
                walletToId={walletToId}
                categoryToId={categoryToId}
                categoryFromId={categoryFromId}
                budgetId={budgetId}
                amountValue={amount}
                transferAmountFromValue={amount}
                transferAmountToValue={valueTo}
                description={description}
                onWalletFromChange={(id) => {
                    setWalletFromId(id);
                    setErrors((prev) => ({ ...prev, form: { ...prev.form, walletFrom: null } }));
                }}
                onWalletToChange={(id) => {
                    setWalletToId(id);
                    setErrors((prev) => ({ ...prev, form: { ...prev.form, walletTo: null } }));
                }}
                onCategoryToChange={(id) => {
                    setCategoryToId(id);
                    setErrors((prev) => ({ ...prev, form: { ...prev.form, categoryTo: null } }));
                }}
                onCategoryFromChange={(id) => {
                    setCategoryFromId(id);
                    setErrors((prev) => ({ ...prev, form: { ...prev.form, categoryFrom: null } }));
                }}
                onBudgetChange={(id) => setBudgetId(id)}
                onAmountChange={(val) => {
                    setAmount(val);
                    setErrors((prev) => ({ ...prev, form: { ...prev.form, amount: null, amountFrom: null } }));
                }}
                onTransferAmountFromChange={(val) => {
                    setAmount(val);
                    setErrors((prev) => ({ ...prev, form: { ...prev.form, amount: null, amountFrom: null } }));
                }}
                onTransferAmountToChange={(val) => {
                    setValueTo(val);
                    setErrors((prev) => ({ ...prev, form: { ...prev.form, amountTo: null } }));
                }}
                onDescriptionChange={(val) => setDescription(val)}
                sameTransferCurrency={sameTransferCurrency}
                fieldErrors={errors.form}
                hideDescription
                hideBudget
            />

            <Box sx={{ m: 2 }}>
                <BudgetSelect
                    selectedBudgetId={budgetId}
                    width="48%"
                    label={t("common.labels.budget")}
                    onChange={(id) => setBudgetId(id)}
                />
            </Box>

            <Box sx={{ m: 2 }}>
                <FormControl variant="standard" sx={{ width: "40%" }}>
                    <InputLabel id="template-rule-type-label">{t("forms.template.repeatType")}</InputLabel>
                    <Select
                        value={ruleType}
                        variant="standard"
                        labelId="template-rule-type-label"
                        onChange={(e) => changeRuleType(e.target.value)}
                        renderValue={getRuleTypeLabel}
                    >
                        <MenuItem value={RULE_TYPES.TYPE_SIMPLE}>{getRuleTypeLabel(RULE_TYPES.TYPE_SIMPLE)}</MenuItem>
                        <MenuItem value={RULE_TYPES.TYPE_DAILY}>{getRuleTypeLabel(RULE_TYPES.TYPE_DAILY)}</MenuItem>
                        <MenuItem value={RULE_TYPES.TYPE_WEEKLY}>{getRuleTypeLabel(RULE_TYPES.TYPE_WEEKLY)}</MenuItem>
                        <MenuItem value={RULE_TYPES.TYPE_MONTHLY}>{getRuleTypeLabel(RULE_TYPES.TYPE_MONTHLY)}</MenuItem>
                    </Select>
                </FormControl>
            </Box>
            {ruleType !== RULE_TYPES.TYPE_SIMPLE && <Box sx={{ m: 2 }}>
                <TextField
                    label={t("common.labels.startDate")}
                    type="date"
                    required
                    error={!!errors.rule?.startDate}
                    helperText={errors.rule?.startDate}
                    value={startDate ?? ""}
                    onChange={(e) => {
                        setStartDate(e.target.value || null);
                        setErrors((prev) => ({ ...prev, rule: { ...prev.rule, startDate: null, endDate: null } }));
                    }}
                    sx={{ width: "48%" }}
                    InputLabelProps={{ shrink: true }}
                />
                <TextField
                    label={t("common.labels.endDate")}
                    type="date"
                    value={endDate || ""}
                    onChange={(e) => {
                        setEndDate(e.target.value || null);
                        setErrors((prev) => ({ ...prev, rule: { ...prev.rule, startDate: null, endDate: null } }));
                    }}
                    sx={{ width: "48%", marginLeft: "12px" }}
                    InputLabelProps={{ shrink: true }}
                />
            </Box>}

            {ruleType === RULE_TYPES.TYPE_DAILY && (
                <Box sx={{ m: 2 }}>
                    <TextField
                        value={dailyRule}
                        onChange={(e) => changeDailyRule(NumberService.getPositiveNumberStr(e.target.value))}
                        type="number"
                        label={t("forms.budget.every")}
                        error={!!errors.rule?.dailyRule}
                        helperText={errors.rule?.dailyRule}
                        variant="standard"
                        sx={{ width: "50%" }}
                    />
                    <Box component="span" sx={{ verticalAlign: "bottom", paddingLeft: 1, paddingTop: "30px", display: "inline-block" }}>
                        {dailyRule === "1" || dailyRule === "" ? t("forms.budget.day") : t("forms.budget.days")}
                    </Box>
                </Box>
            )}
            {ruleType === RULE_TYPES.TYPE_WEEKLY && (
                <Box sx={{ m: 2 }}>
                    {weeklyRule.map((_, i) => (
                        <Button
                            key={i}
                            variant="outlined"
                            size="small"
                            sx={{
                                borderColor: weeklyRule[i] ? "primary.main" : "primary.contrastText",
                                padding: "2px 10px",
                                margin: "3px",
                                minWidth: 0,
                                color: errors.rule?.weeklyRule ? "error.main" : "primary.main",
                            }}
                            onClick={() => changeWeeklyRule(i)}
                        >
                            {DAYS_OF_WEEK[i]}
                        </Button>
                    ))}
                    {errors.rule?.weeklyRule && (
                        <Typography variant="caption" color="error" display="block">
                            {errors.rule.weeklyRule}
                        </Typography>
                    )}
                </Box>
            )}
            {ruleType === RULE_TYPES.TYPE_MONTHLY && (
                <Box sx={{ m: 2 }}>
                    {monthlyRule.map((_, i) => (
                        <Button
                            key={i}
                            variant="outlined"
                            size="small"
                            sx={{
                                borderColor: monthlyRule[i] ? "primary.main" : "primary.contrastText",
                                padding: "2px 10px",
                                margin: "3px",
                                minWidth: 0,
                                width: 50,
                                color: errors.rule?.monthlyRule ? "error.main" : "primary.main",
                            }}
                            onClick={() => changeMonthlyRule(i)}
                        >
                            {i + 1}
                        </Button>
                    ))}
                    {errors.rule?.monthlyRule && (
                        <Typography variant="caption" color="error" display="block">
                            {errors.rule.monthlyRule}
                        </Typography>
                    )}
                </Box>
            )}
            </CardContent>
            <CardActions sx={{ ml: "2rem", mb: "1rem", mt: "1rem", mr: "2rem", height: "4rem" }}>
                <Box sx={{ position: "relative", width: "100%" }}>
                    <Box sx={{ position: "absolute", left: 0 }}>
                        <Button variant="outlined" sx={{ mr: "1rem" }} onClick={handleCancel}>
                            {t("common.buttons.cancel")}
                        </Button>
                        <Button variant="contained" onClick={handleSave}>
                            {t("common.buttons.save")}
                        </Button>
                    </Box>
                    {template.id && (
                        <Box sx={{ position: "absolute", right: 0 }}>
                            <Button
                                variant="outlined"
                                color="error"
                                onClick={() => dispatch(deleteTemplate(template, () => onCancel()))}
                            >
                                {t("common.buttons.delete")}
                            </Button>
                        </Box>
                    )}
                </Box>
            </CardActions>
        </>
    );

    return (
        <ViewAndEditFormLayout
            editTitle={template.id ? t("forms.template.editTitle") : t("forms.template.createTitle")}
            viewTitle={template.id ? t("forms.template.editTitle") : t("forms.template.createTitle")}
            initialEdit={true}
            onCancel={onCancel}
            onSave={onSaveClick}
            onDelete={template.id ? () => dispatch(deleteTemplate(template, () => onCancel())) : undefined}
            editContent={editContent}
            viewContent={null}
        />
    );
};

export default EditTemplateForm;
